import { Box, Button, Skeleton, Stack, Typography } from '@mui/material';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState, type Ref } from 'react';
import { QuilttProvider } from '@quiltt/react';

import WhyImportantLink from '../components/WhyImportantLink';
import { ACCOUNTING_SETUP_HELP_CONTEXT } from '../hooks/useOpenAccountingSetupHelp';

import useResolvabilityCheckSession from './hooks/useResolvabilityCheckSession';
import useConnectStatementsData, {
    AUTO_STATEMENTS_SKIPPED_EVENT,
    AUTO_STATEMENTS_SKIP_REASON,
    getPersistedQuilttConnections,
    type ConnectStatementsInstitution,
} from './hooks/useConnectStatementsData';
import useInstitutionSupport from './hooks/useInstitutionSupport';
import InstitutionStatementCard from './components/InstitutionStatementCard';
import SyncStatementsCard from './components/SyncStatementsCard';
import EligibleStatementsModal from './components/EligibleStatementsModal';
import { isConnectStatementsUntouched } from './visibility';

import InstitutionResolvabilityCheckers from 'services/quiltt/utils/InstitutionResolvabilityCheckers';
import useCompleteOnboardingStep from 'services/bookkeeping/mutations/useCompleteOnboardingStep';
import useUpdateOnboardingStep from 'services/bookkeeping/mutations/useUpdateOnboardingStep';
import { useAccountingOnboardingProgress } from 'services/bookkeeping/queries/useAccountingOnboardingProgress';
import AutoStatementsSuccessModal from 'modules/Settings/BankAccountsCombined/components/AutoStatementsSuccessModal';
import useEventTracking from 'modules/common/components/AnalyticsTracking/useEventTracking';

const ONBOARDING_STEP = 'connect_automated_statements';

// Member-driven only; the automatic skip sends AUTO_STATEMENTS_SKIPPED_EVENT.
export const STATEMENTS_STEP_RESOLVED_EVENT = 'Statements Step Resolved';

/**
 * `cards` is the accounting wizard's step: one card per institution, each with its own Enable.
 * `sync-list` is business accounts setup under the V1 redesign: one list of the banks the
 * member just connected, and a single Sync statements action that opens Quiltt already searching for
 * the next one — so they are never asked to find a bank they connected a step ago.
 */
export type ConnectStatementsLayout = 'cards' | 'sync-list';

interface ConnectStatementsProps {
    // StepWizard2 helpers (subset we use)
    readonly helpers: {
        setCanProceed: (canProceed: boolean) => void;
        /** The wizard's own view of canProceed, which lags this step's by a render. */
        canProceed?: boolean;
        onNext?: () => void;
        onCanSkipChange?: (canSkip: boolean) => void;
    };
    // Defaulted in the forwardRef destructure, which the rule cannot see.
    // eslint-disable-next-line react/require-default-props
    readonly layout?: ConnectStatementsLayout;
}

export interface ConnectStatementsRef {
    handleContinue: () => Promise<void>;
    handleSkip: () => void;
    isCompleting: boolean;
    handleStepExit: () => Promise<boolean>;
}

interface ConnectStatementsInnerProps extends Omit<ConnectStatementsProps, 'layout'> {
    readonly layout: ConnectStatementsLayout;
    readonly connectorId: string | undefined;
    readonly armTimeout: boolean;
    readonly forwardedRef: Ref<ConnectStatementsRef>;
}

function ConnectStatementsInner({
    helpers,
    layout,
    connectorId,
    armTimeout,
    forwardedRef,
}: Readonly<ConnectStatementsInnerProps>) {
    const { canProceed: wizardCanProceed, onCanSkipChange } = helpers;
    // StepWizard2 rebuilds setCanProceed and onNext every render; read them through a ref so effects
    // don't re-fire on identity, and onNext after an await reaches the latest handleNext.
    const helpersRef = useRef(helpers);
    helpersRef.current = helpers;
    const { institutions, isLoading } = useConnectStatementsData();
    const { supportedInstitutionIds, allRealChecksComplete, allSettled, getResolvable, isTimedOut } =
        useInstitutionSupport(institutions, connectorId, isLoading, { armTimeout });
    const { data: onboardingProgress } = useAccountingOnboardingProgress();
    // On the event too, since the profile property only reaches events sent after the identify lands.
    const setupVersion = onboardingProgress?.setup_version;

    const completeStep = useCompleteOnboardingStep();
    const updateStep = useUpdateOnboardingStep();
    const { sendTrackingEvent } = useEventTracking();

    const [enabledInstitutionIds, setEnabledInstitutionIds] = useState<Set<string>>(new Set());
    const [connectionIdByInstitution, setConnectionIdByInstitution] = useState<Record<string, string>>({});
    const [successInstitutionName, setSuccessInstitutionName] = useState<string | null>(null);
    const [showSkipModal, setShowSkipModal] = useState(false);
    const [isCompleting, setIsCompleting] = useState(false);
    // Set after a confirmed or automatic skip — unlocks canProceed and triggers the navigation effect.
    const [skipNavRequested, setSkipNavRequested] = useState(false);
    const hasAutoSkippedRef = useRef(false);
    const hasSeededRef = useRef(false);
    // The skip controls stay enabled while the save runs, so a second click must not start another.
    const isSkipSavingRef = useRef(false);

    // Re-hydrate enablement on re-entry: enabledInstitutionIds is session-local, so without this a member who
    // already connected would see the card as SUPPORTED again on return. Seed once from the step's own
    // persisted record (kept fresh by the Continue mutation's invalidation); union-merge so a fresh in-session
    // enable is never clobbered.
    useEffect(() => {
        if (hasSeededRef.current || !onboardingProgress) {
            return;
        }
        const persisted = getPersistedQuilttConnections(onboardingProgress);
        if (!persisted.length) {
            return;
        }
        hasSeededRef.current = true;
        setEnabledInstitutionIds((prev) => {
            const next = new Set(prev);
            persisted.forEach(({ institutionId }) => next.add(institutionId));
            return next;
        });
        setConnectionIdByInstitution((prev) => {
            const next = { ...prev };
            persisted.forEach(({ institutionId, connectionId }) => {
                if (connectionId) {
                    next[institutionId] = connectionId;
                }
            });
            return next;
        });
    }, [onboardingProgress]);

    // Unlocks on enablement, settled-with-nothing-to-enable, or a confirmed skip (so handleNext can navigate).
    const canProceed =
        skipNavRequested || enabledInstitutionIds.size > 0 || (allSettled && supportedInstitutionIds.length === 0);
    useEffect(() => {
        helpersRef.current.setCanProceed(canProceed);
    }, [canProceed]);

    // Once any institution is enabled, skipping the whole step is contradictory — only Continue remains.
    const canSkip = enabledInstitutionIds.size === 0;
    useEffect(() => {
        onCanSkipChange?.(canSkip);
    }, [canSkip, onCanSkipChange]);

    // While support checks run, show the skeleton — avoids flashing NOT-SUPPORTED cards before an auto-skip.
    const isResolving = !isLoading && institutions.length > 0 && !allSettled && enabledInstitutionIds.size === 0;

    // Nothing to enable → skip the step entirely (product: no Relay upsell). Only a GENUINE all-not-supported
    // result triggers the skip; a timeout never does, so a slow-but-supported bank is never silently skipped.
    // Only an untouched step: a member's earlier enable is re-hydrated a render late, and a bank withdrawn
    // since then already reads not-resolvable on that first render.
    const shouldAutoSkip =
        !isLoading &&
        isConnectStatementsUntouched(onboardingProgress) &&
        enabledInstitutionIds.size === 0 &&
        (institutions.length === 0 || (allRealChecksComplete && supportedInstitutionIds.length === 0));

    const showInstitutions = !isLoading && !isResolving && !shouldAutoSkip && institutions.length > 0;

    useEffect(() => {
        if (!shouldAutoSkip || hasAutoSkippedRef.current) {
            return;
        }
        hasAutoSkippedRef.current = true;
        sendTrackingEvent(AUTO_STATEMENTS_SKIPPED_EVENT, {
            category: 'accounting_setup',
            action: 'auto_statements_skipped',
            skip_reason: AUTO_STATEMENTS_SKIP_REASON,
            stepName: 'connect_statements',
            setup_version: setupVersion,
        });
        // Persist skipped with skip_reason (deliberate skips stay bare), then advance regardless of outcome.
        // Advance through skipNavRequested, not by calling onNext here: StepWizard2's handleNext is gated on
        // the wizard's canProceed, which is still false when the skip is decided on first render (a warm
        // support-check cache), so a direct call would be dropped and leave the member on the skeleton.
        updateStep
            .mutateAsync({
                step: ONBOARDING_STEP,
                status: 'skipped',
                data: { skip_reason: AUTO_STATEMENTS_SKIP_REASON },
            })
            .catch(() => undefined)
            .finally(() => setSkipNavRequested(true));
    }, [shouldAutoSkip, updateStep, sendTrackingEvent, setupVersion]);

    // Shared payload builder so enable-time and Continue write the same shape (can't drift).
    const buildStepData = useCallback(
        (enabledIds: Set<string>, connectionIds: Record<string, string>) => {
            const enabled = institutions.filter((inst) => enabledIds.has(inst.institutionId));
            return {
                linked_accounts_count: enabled.reduce((count, inst) => count + inst.accounts.length, 0),
                linked_accounts: enabled.flatMap((inst) =>
                    inst.accounts.map((account) => ({
                        id: account.id,
                        name: account.name,
                        type: account.type,
                        mask: account.mask,
                        institution_name: account.institutionName,
                        institution_id: account.institutionId,
                    }))
                ),
                // connectionId is optional — drop institutions without one instead of emitting undefined.
                quiltt_connections: enabled.flatMap((inst) => {
                    const connectionId = connectionIds[inst.institutionId];
                    if (!connectionId) {
                        return [];
                    }
                    return [
                        {
                            connection_id: connectionId,
                            institution_id: inst.institutionId,
                            institution_name: inst.institutionName,
                        },
                    ];
                }),
            };
        },
        [institutions]
    );

    const handleEnabled = useCallback(
        (institutionId: string, connectionId?: string) => {
            // Compute next state explicitly (setState is async) so we can persist the just-enabled institution now.
            const nextEnabledIds = new Set(enabledInstitutionIds).add(institutionId);
            const nextConnectionIds = connectionId
                ? { ...connectionIdByInstitution, [institutionId]: connectionId }
                : connectionIdByInstitution;

            setEnabledInstitutionIds(nextEnabledIds);
            setConnectionIdByInstitution(nextConnectionIds);

            const institution = institutions.find((inst) => inst.institutionId === institutionId);
            if (institution) {
                setSuccessInstitutionName(institution.institutionName);
            }

            // Persist immediately with the connectionId Quiltt just handed us — don't wait for Continue. A member
            // who enables then exits before Continue must still return to an ENABLED card; the seed effect
            // re-hydrates from this record. Fire-and-forget — the mutation hook surfaces any error.
            updateStep
                .mutateAsync({
                    step: ONBOARDING_STEP,
                    status: 'in_progress',
                    data: buildStepData(nextEnabledIds, nextConnectionIds),
                })
                .catch(() => undefined);
        },
        [enabledInstitutionIds, connectionIdByInstitution, institutions, updateStep, buildStepData]
    );

    // Pre-save status; the save invalidates it.
    const persistedStatus = onboardingProgress?.connect_automated_statements_status;

    // Sent once the step's status is saved, so a failed write is not counted.
    const trackResolved = useCallback(
        (outcome: 'connected' | 'skipped', skipTrigger?: 'continue' | 'skip') => {
            // Re-entering a resolved step and leaving it the same way is not a new resolution.
            if (persistedStatus === (outcome === 'connected' ? 'completed' : 'skipped')) {
                return;
            }
            sendTrackingEvent(STATEMENTS_STEP_RESOLVED_EVENT, {
                category: 'accounting_setup',
                outcome,
                source_surface: layout === 'sync-list' ? 'onboarding_biz_account_setup' : 'onboarding_accounting_setup',
                // Quiltt enables, not webhook-confirmed connections.
                enabled_institutions_count: enabledInstitutionIds.size,
                setup_version: setupVersion,
                ...(skipTrigger && { skip_trigger: skipTrigger }),
            });
        },
        [persistedStatus, sendTrackingEvent, layout, enabledInstitutionIds, setupVersion]
    );

    const handleContinue = useCallback(async () => {
        setIsCompleting(true);
        try {
            if (enabledInstitutionIds.size === 0) {
                // Nothing synced (no bank supported, or every check timed out): record the manual-upload skip.
                await updateStep.mutateAsync({ step: ONBOARDING_STEP, status: 'skipped' });
                trackResolved('skipped', 'continue');
            } else {
                await completeStep.mutateAsync({
                    step: ONBOARDING_STEP,
                    // Record only — the running flow trusts webhook-confirmed connection_status, not this attempt.
                    data: buildStepData(enabledInstitutionIds, connectionIdByInstitution),
                });
                trackResolved('connected');
            }
            helpersRef.current.onNext?.();
        } finally {
            setIsCompleting(false);
        }
    }, [enabledInstitutionIds, connectionIdByInstitution, completeStep, updateStep, buildStepData, trackResolved]);

    const confirmSkip = useCallback(async () => {
        if (isSkipSavingRef.current) {
            return;
        }
        isSkipSavingRef.current = true;
        setShowSkipModal(false);
        try {
            // Await persistence before advancing so a failed skip doesn't desync the wizard.
            await updateStep.mutateAsync({ step: ONBOARDING_STEP, status: 'skipped' });
            trackResolved('skipped', 'skip');
            // Flag instead of calling onNext() inline — handleNext is gated on the wizard's canProceed,
            // which has not caught up yet. Success-only, so a failed persist keeps the member on the step.
            setSkipNavRequested(true);
        } catch {
            // The mutation hook surfaces the error (snackbar + Datadog); stay on the step to retry.
        } finally {
            isSkipSavingRef.current = false;
        }
    }, [updateStep, trackResolved]);

    // Advance once the wizard has seen canProceed unlock, so its handleNext lets the call through. Keyed
    // on canAdvance alone so it fires once — on the last step, each extra call would complete it again.
    // Hosts that do not report the wizard's canProceed fall back to this step's own.
    const canAdvance = skipNavRequested && (wizardCanProceed ?? canProceed);
    useEffect(() => {
        if (canAdvance) {
            helpersRef.current.onNext?.();
            // Consume the request: if onNext did not navigate, a later skip must be able to re-trigger it.
            setSkipNavRequested(false);
        }
    }, [canAdvance]);

    const handleSkip = useCallback(() => {
        // Warn only when the member is leaving eligible (supported, not-yet-enabled) accounts behind.
        const hasEligibleNotEnabled = supportedInstitutionIds.some((id) => !enabledInstitutionIds.has(id));
        if (hasEligibleNotEnabled) {
            setShowSkipModal(true);
        } else {
            // confirmSkip swallows its own errors; the returned promise never rejects.
            confirmSkip();
        }
    }, [supportedInstitutionIds, enabledInstitutionIds, confirmSkip]);

    useImperativeHandle(
        forwardedRef,
        () => ({ handleContinue, handleSkip, isCompleting, handleStepExit: async () => true }),
        [handleContinue, handleSkip, isCompleting]
    );

    return (
        <Box sx={{ width: '100%', maxWidth: layout === 'sync-list' ? 560 : 720, mx: 'auto' }}>
            {/* Invisible checkers report into the connector-keyed cache (already inside this step's
                QuilttProvider). Already-enabled institutions short-circuit the flaky check. */}
            <InstitutionResolvabilityCheckers
                institutions={institutions}
                connectorId={connectorId}
                isEnabled={(institutionId) => enabledInstitutionIds.has(institutionId)}
            />

            {layout === 'sync-list' ? (
                <Stack spacing={1} sx={{ mb: 2 }}>
                    <Typography variant="h1">Automatically import your monthly statements</Typography>
                    <Typography variant="body1">
                        We&apos;ll automatically pull in your bank statements each month to keep your books complete and
                        reports accurate.
                    </Typography>
                    <Typography variant="body1">
                        You&apos;ll still need to upload your latest statements today to get set up, but going forward,
                        you won&apos;t need to download and upload statements every month. Hooray!
                    </Typography>
                    <Box>
                        <WhyImportantLink
                            context={ACCOUNTING_SETUP_HELP_CONTEXT.connectStatements}
                            title="What happens if I don't set up Auto Statements?"
                            helpQuestion="What happens if I don't set up Auto Statements with Collective?"
                        />
                    </Box>
                </Stack>
            ) : (
                <Stack spacing={1} sx={{ mb: 3 }}>
                    <Typography variant="h1">Enable Auto Statements</Typography>
                    <Typography variant="subtitle1" color="text.secondary">
                        Your bank statements are how we make sure every transaction is accounted for each month. Connect
                        your accounts below to pull them automatically.
                    </Typography>
                    <Box>
                        <WhyImportantLink
                            context={ACCOUNTING_SETUP_HELP_CONTEXT.connectStatements}
                            title="Why are statements important?"
                            helpQuestion="Why are bank statements important for keeping my books accurate with Collective?"
                        />
                    </Box>
                </Stack>
            )}

            {(isLoading || isResolving || shouldAutoSkip) && (
                <Stack spacing={2}>
                    {[1, 2].map((key) => (
                        <Skeleton key={key} variant="rectangular" height={140} sx={{ borderRadius: 1.5 }} />
                    ))}
                    {/* The sync list carries its own skip, so it has to stay reachable while checks run.
                        handleSkip still warns first if a bank has already come back supported. */}
                    {layout === 'sync-list' && isResolving && (
                        <Box>
                            <Button variant="text" color="secondary" onClick={handleSkip}>
                                Upload statements manually
                            </Button>
                        </Box>
                    )}
                </Stack>
            )}

            {showInstitutions && layout === 'sync-list' && (
                <SyncStatementsCard
                    institutions={institutions}
                    getResolvable={getResolvable}
                    isTimedOut={isTimedOut}
                    enabledInstitutionIds={enabledInstitutionIds}
                    connectorId={connectorId}
                    onEnabled={handleEnabled}
                    onUploadManually={handleSkip}
                />
            )}

            {showInstitutions && layout === 'cards' && (
                <Stack spacing={2.5}>
                    {institutions.map((institution: ConnectStatementsInstitution) => (
                        <InstitutionStatementCard
                            key={institution.institutionId}
                            institution={institution}
                            isResolvable={getResolvable(institution.institutionId)}
                            isEnabled={enabledInstitutionIds.has(institution.institutionId)}
                            connectorId={connectorId}
                            onEnabled={handleEnabled}
                        />
                    ))}
                </Stack>
            )}

            {/* The sync list shows its own progress dialog and snackbar; this one belongs to the cards. */}
            <AutoStatementsSuccessModal
                open={layout === 'cards' && successInstitutionName !== null}
                onClose={() => setSuccessInstitutionName(null)}
                institutionName={successInstitutionName ?? ''}
            />
            <EligibleStatementsModal
                open={showSkipModal}
                onClose={() => setShowSkipModal(false)}
                onConfirm={confirmSkip}
            />
        </Box>
    );
}

/**
 * Auto Statements onboarding step. Wraps the content in a QuilttProvider so the resolvability checks
 * and the connect flow have a Quiltt session. Support is determined at the institution level via
 * `useQuilttResolvable`; accounts come from the onboarding-native confirmed_business_accounts.
 */
const ConnectStatements = forwardRef<ConnectStatementsRef, ConnectStatementsProps>(function ConnectStatements(
    { helpers, layout = 'cards' },
    ref
) {
    const { token, connectorId, armTimeout } = useResolvabilityCheckSession();

    return (
        <QuilttProvider token={token}>
            <ConnectStatementsInner
                helpers={helpers}
                layout={layout}
                connectorId={connectorId}
                armTimeout={armTimeout}
                forwardedRef={ref}
            />
        </QuilttProvider>
    );
});

export default ConnectStatements;
